import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import { getProductImage } from '../../utils/productUtils';
import { 
  TextField, 
  Button, 
  Typography, 
  Box,
  Alert,
  CircularProgress,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  Grid,
  Paper,
  IconButton
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon
} from '@mui/icons-material';

export default function WarehouseLoadForm() {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productId, setProductId] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [purchasePrice, setPurchasePrice] = useState('');
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Dialog e stato dei prezzi
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogStep, setDialogStep] = useState(0);
  const [priceData, setPriceData] = useState({
    oldPurchasePrice: 0,
    oldSellingPrice: 0,
    oldSellingPriceWithLabor: 0,
    newPurchasePrice: 0,
    updatedPurchasePrice: 0,
    updatedSellingPrice: 0,
    assemblyTime: 0,
    laborHourlyRate: 0,
    sellingPriceWithLabor: 0,
    quantityLoaded: 0,
    action: ''
  });
  
  const [shouldLoadAfterClose, setShouldLoadAfterClose] = useState(false);

  // useEffect per eseguire doLoad dopo la chiusura del dialogo
  useEffect(() => {
    if (!dialogOpen && shouldLoadAfterClose) {
      setShouldLoadAfterClose(false);
      setTimeout(() => {
        doLoad();
      }, 50);
    }
  }, [dialogOpen, shouldLoadAfterClose]);

  // Carica i prodotti all'avvio
  useEffect(() => {
    loadProducts();
    loadLaborHourlyRate();
  }, []);

  // Funzione per caricare la tariffa oraria
  const loadLaborHourlyRate = async () => {
    try {
      const res = await api.get('/company-settings');
      if (res.data && res.data.laborHourlyRate) {
        setPriceData(prev => ({
          ...prev,
          laborHourlyRate: parseFloat(res.data.laborHourlyRate) || 0
        }));
      }
    } catch (err) {
      console.error('Errore nel caricamento tariffa oraria:', err);
      setError('Errore nel caricamento della tariffa oraria');
    }
  };

  // Carica i prodotti dal server
  const loadProducts = async () => {
    try {
      const res = await api.get('/products', {
        params: { all: 'true' }
      });
      
      const productsArray = res.data.products || res.data || [];
      const filteredProducts = Array.isArray(productsArray) 
        ? productsArray.filter(p => !p.isLabor)
        : [];
      
      setProducts(filteredProducts);
      setFilteredProducts(filteredProducts);
    } catch (err) {
      console.error('Errore nel caricamento prodotti:', err);
      setError('Errore nel caricamento dei prodotti');
    }
  };

  // Funzione per caricare un singolo prodotto
  const loadSingleProduct = async (id) => {
    try {
      const res = await api.get(`/products/${id}`);
      if (res.data) {
        setProducts(prev => {
          const updated = [...prev];
          const index = updated.findIndex(p => p.id === id);
          if (index !== -1) {
            updated[index] = res.data;
          }
          return updated;
        });
        setSelectedProduct(res.data);
        return res.data;
      }
      return null;
    } catch (err) {
      console.error(`Errore nel caricamento del prodotto ${id}:`, err);
      return null;
    }
  };

  // Filtra prodotti in base alla ricerca
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredProducts([]);
      return;
    }

    const term = searchTerm.toLowerCase().trim();
    const filtered = products.filter(p => {
      const nameMatch = p.name?.toLowerCase().includes(term);
      const codeMatch = p.code?.toLowerCase().includes(term);
      return nameMatch || codeMatch;
    });
    
    setFilteredProducts(filtered);
  }, [searchTerm, products]);

  // Gestione selezione prodotto dalla ricerca
  const handleProductSelect = async (product) => {
    if (!product) return;
    
    setSelectedProduct(product);
    setProductId(product.id);
    setSearchTerm(`${product.code} - ${product.name}`);
    
    // Carica i dati freschi dal DB
    const freshProduct = await loadSingleProduct(product.id);
    if (freshProduct) {
      setPurchasePrice(freshProduct.purchasePrice || '');
    } else {
      setPurchasePrice(product.purchasePrice || '');
    }
    
    setFilteredProducts([]);
  };


  // Reset completo dello stato dopo operazioni
  const resetState = () => {
    setProductId('');
    setQuantity(1);
    setPurchasePrice('');
    setSelectedProduct(null);
    setSearchTerm('');
    setFilteredProducts([]);
    setPriceData({
      oldPurchasePrice: 0,
      oldSellingPrice: 0,
      oldSellingPriceWithLabor: 0,
      newPurchasePrice: 0,
      updatedPurchasePrice: 0,
      updatedSellingPrice: 0,
      assemblyTime: 0,
      laborHourlyRate: priceData.laborHourlyRate,
      sellingPriceWithLabor: 0,
      quantityLoaded: 0,
      action: ''
    });
    setDialogStep(0);
    setDialogOpen(false);
    setError('');
  };

  // handlePurchasePriceChange aggiorna solo lo stato
  const handlePurchasePriceChange = (e) => {
    setPurchasePrice(e.target.value);
  };

  // Gestione del submit
  const handleSubmit = async () => {
    if (!productId || quantity <= 0 || !purchasePrice) {
      setError('Inserire prodotto, quantità e prezzo di acquisto validi');
      return;
    }

    const product = await loadSingleProduct(productId);
    if (!product) {
      setError('Prodotto non trovato o errore nel caricamento');
      return;
    }
    
    // Se il prezzo di acquisto è cambiato, apri il dialog
    if (parseFloat(purchasePrice) !== parseFloat(product.purchasePrice || 0)) {
      const laborCostSubmit = (product.assemblyTime || 0) / 60 * priceData.laborHourlyRate;
      const sellingPriceWithLabor = parseFloat(product.sellingPrice || 0) + laborCostSubmit;
      
      setPriceData({
        ...priceData,
        oldPurchasePrice: parseFloat(product.purchasePrice || 0),
        oldSellingPrice: parseFloat(product.sellingPrice || 0),
        oldSellingPriceWithLabor: sellingPriceWithLabor,
        newPurchasePrice: parseFloat(purchasePrice),
        assemblyTime: parseFloat(product.assemblyTime || 0),
        quantityLoaded: quantity,
        action: ''
      });
      
      setDialogStep(0);
      setDialogOpen(true);
    } else {
      doLoad();
    }
  };

  // Gestione delle opzioni di prezzo
  const handlePriceOption = async (action) => {
    setLoading(true);
    
    try {
      const response = await api.post('/products/update-prices-on-load', {
        productId,
        oldPurchasePrice: priceData.oldPurchasePrice,
        newPurchasePrice: priceData.newPurchasePrice,
        quantityLoaded: priceData.quantityLoaded,
        action,
        forceRecalculation: action === priceData.action
      });
      
      if (response.data && response.data.product) {
        const productData = response.data.product;
        
        if (action === 'none') {
          setPriceData(prev => ({
            ...prev,
            action: 'none'
          }));
          setShouldLoadAfterClose(true);
          setDialogOpen(false);
          return;
        }
        
        const assemblyTime = productData.assemblyTime || 
                          (selectedProduct ? selectedProduct.assemblyTime : 0);
        
        const laborCostOption = (assemblyTime / 60) * priceData.laborHourlyRate;
        const sellingPriceWithLabor = parseFloat(productData.updatedSellingPrice || 0) + laborCostOption;
        
        setPriceData(prev => ({
          ...prev,
          updatedPurchasePrice: parseFloat(productData.updatedPurchasePrice || 0),
          updatedSellingPrice: parseFloat(productData.updatedSellingPrice || 0),
          assemblyTime: parseFloat(assemblyTime) || 0,
          sellingPriceWithLabor: sellingPriceWithLabor,
          action
        }));
        
        setTimeout(() => {
          setDialogStep(1);
          setLoading(false);
        }, 300);
      } else {
        setLoading(false);
      }
    } catch (err) {
      console.error('Errore nel calcolo prezzi:', err);
      setError('Errore durante il calcolo dei prezzi: ' + (err.response?.data?.error || err.message));
      setLoading(false);
    }
  };

  // Gestione del cambio di valori nei campi di prezzo
  const handlePriceFieldChange = (e) => {
    const { name, value } = e.target;
    const numValue = parseFloat(value) || 0;
    const updatedData = { ...priceData, [name]: numValue };
    
    if (name === 'updatedSellingPrice') {
      const laborCost = (updatedData.assemblyTime / 60) * updatedData.laborHourlyRate;
      updatedData.sellingPriceWithLabor = parseFloat((updatedData.updatedSellingPrice + laborCost).toFixed(2));
    } else if (name === 'assemblyTime') {
      const laborCost = (updatedData.assemblyTime / 60) * updatedData.laborHourlyRate;
      updatedData.sellingPriceWithLabor = parseFloat((updatedData.updatedSellingPrice + laborCost).toFixed(2));
    } else if (name === 'sellingPriceWithLabor') {
      const basePrice = updatedData.updatedSellingPrice;
      const laborDifference = updatedData.sellingPriceWithLabor - basePrice;
      if (updatedData.laborHourlyRate > 0) {
        updatedData.assemblyTime = parseFloat(((laborDifference / updatedData.laborHourlyRate) * 60).toFixed(2));
      }
    }
    
    setPriceData(updatedData);
  };

  // Conferma i prezzi aggiornati e procede con il carico
  const confirmPrices = async () => {
    setLoading(true);
    setError('');
    try {
      const productResponse = await api.get(`/products/${productId}`);
      const existingProduct = productResponse.data;
      
      if (!existingProduct) {
        throw new Error('Prodotto non trovato');
      }
      
      const assemblyTimeValue = Math.max(0, parseFloat(priceData.assemblyTime) || 0);
      
      await api.put(`/products/${productId}`, {
        ...existingProduct,
        sellingPrice: priceData.updatedSellingPrice,
        purchasePrice: priceData.updatedPurchasePrice,
        assemblyTime: assemblyTimeValue
      });
      
      setPurchasePrice(priceData.updatedPurchasePrice.toString());
      
      setDialogOpen(false);
      setDialogStep(0);
      
      await loadProducts();
      await doLoad();
    } catch (err) {
      console.error('Errore nell\'aggiornamento prezzi:', err);
      setError('Errore durante l\'aggiornamento prezzi: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Chiusura del dialog
  const handleCloseDialog = () => {
    if (!loading) {
      setDialogOpen(false);
      setDialogStep(0);
    }
  };

  // Quando si torna indietro dal secondo step del dialog
  const handleBack = () => {
    setLoading(true);
    
    setPriceData({
      ...priceData,
      action: ''
    });
    
    setTimeout(() => {
      setDialogStep(0);
      setLoading(false);
    }, 100);
  };

  // Caricamento effettivo del prodotto
  const doLoad = async () => {
    setLoading(true);
    setError('');
    setSuccess(null);
    try {
      if (!productId || quantity <= 0 || !purchasePrice) {
        throw new Error('Dati di carico incompleti');
      }

      await api.post('/warehouse-movements/load', {
        productId, 
        quantity,
        purchasePrice: parseFloat(purchasePrice),
        notes: 'Carico manuale'
      });
      
      setSuccess(true);
      resetState();
      await loadProducts();
      
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (err) {
      console.error('Errore nel carico:', err);
      setError(err.response?.data?.error || 'Errore durante il carico');
      setSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Carico Manuale Magazzino
      </Typography>

      {/* Campo di ricerca prodotto */}
      <Box sx={{ position: 'relative', my: 2 }}>
        <TextField
          fullWidth
          label="Cerca prodotto per nome o codice"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            if (!e.target.value) {
              setSelectedProduct(null);
              setProductId('');
              setPurchasePrice('');
            }
          }}
          disabled={loading}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedProduct(null);
                    setProductId('');
                    setPurchasePrice('');
                    setFilteredProducts([]);
                  }}
                >
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            )
          }}
        />
        
        {/* Risultati ricerca in tempo reale */}
        {filteredProducts.length > 0 && (
          <Paper
            sx={{
              position: 'absolute',
              zIndex: 1000,
              mt: 1,
              maxHeight: 300,
              overflow: 'auto',
              width: '100%',
              boxShadow: 3
            }}
          >
            {filteredProducts.map((product) => (
              <Box
                key={product.id}
                onClick={() => handleProductSelect(product)}
                sx={{
                  p: 2,
                  cursor: 'pointer',
                  borderBottom: '1px solid #e0e0e0',
                  '&:hover': {
                    bgcolor: 'action.hover'
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    component="img"
                    src={getProductImage(product.image)}
                    alt={product.name}
                    sx={{
                      width: 50,
                      height: 50,
                      objectFit: 'contain',
                      bgcolor: '#f5f5f5',
                      borderRadius: 1
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight="bold">
                      {product.code}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {product.name}
                    </Typography>
                    {product.stock !== undefined && (
                      <Typography variant="caption" color="text.secondary">
                        Giacenza: {product.stock}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            ))}
          </Paper>
        )}
      </Box>

      {/* Informazioni prodotto selezionato */}
      {selectedProduct && (
        <Box sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            Prodotto selezionato:
          </Typography>
          <Typography variant="body2">
            <strong>{selectedProduct.code}</strong> - {selectedProduct.name}
          </Typography>
          {selectedProduct.stock !== undefined && (
            <Typography variant="body2" color="text.secondary">
              Giacenza attuale: {selectedProduct.stock}
            </Typography>
          )}
        </Box>
      )}

      <TextField
        type="number"
        label="Quantità da caricare"
        value={quantity}
        onChange={(e) => {
          const value = e.target.value;
          if (value === '') {
            setQuantity('');
          } else {
            const numValue = parseInt(value);
            if (!isNaN(numValue) && numValue >= 1) {
              setQuantity(numValue);
            }
          }
        }}
        onBlur={(e) => {
          if (quantity === '' || quantity < 1) {
            setQuantity(1);
          }
        }}
        fullWidth
        sx={{ mb: 2 }}
        disabled={loading || !selectedProduct}
        InputProps={{ inputProps: { min: 1 } }}
      />

      <TextField
        type="number"
        label="Prezzo di acquisto"
        value={purchasePrice}
        onChange={handlePurchasePriceChange}
        fullWidth
        sx={{ mb: 2 }}
        disabled={loading || !selectedProduct}
        InputProps={{
          startAdornment: <InputAdornment position="start">€</InputAdornment>,
          inputProps: { min: 0, step: "0.01" }
        }}
      />

      {/* Dialog 1: Selezione azione */}
      <Dialog 
        open={dialogOpen && dialogStep === 0}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Variazione prezzo di acquisto
        </DialogTitle>
        
        <DialogContent>
          <Box>
            <Typography gutterBottom>
              Il prezzo di acquisto è variato da <b>€{priceData.oldPurchasePrice.toFixed(2)}</b> a <b>€{priceData.newPurchasePrice.toFixed(2)}</b>
            </Typography>
            <Typography sx={{ mb: 2 }}>Come vuoi procedere?</Typography>
            
            <Button 
              fullWidth 
              sx={{ mt: 2 }} 
              onClick={() => handlePriceOption('average')} 
              variant="contained" 
              color="primary"
              disabled={loading}
            >
              {loading ? <CircularProgress size={20} /> : 'Aggiorna con la media ponderata'}
            </Button>
            
            <Button 
              fullWidth 
              sx={{ mt: 2 }} 
              onClick={() => handlePriceOption('none')} 
              variant="outlined"
              color="secondary"
              disabled={loading}
            >
              {loading ? <CircularProgress size={20} /> : 'Lascia i prezzi invariati'}
            </Button>
          </Box>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={loading}>
            Annulla
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog 2: Modifica prezzi */}
      <Dialog 
        open={dialogOpen && dialogStep === 1}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Modifica prezzi di vendita
        </DialogTitle>
        
        <DialogContent>
          {dialogStep === 1 && (
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Aggiornamento con la media ponderata
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="Prezzo di acquisto (€)"
                    type="number"
                    fullWidth
                    margin="normal"
                    name="updatedPurchasePrice"
                    value={priceData.updatedPurchasePrice}
                    disabled
                    InputProps={{
                      startAdornment: <InputAdornment position="start">€</InputAdornment>,
                      readOnly: true
                    }}
                  />
                </Grid>
                <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center', pl: 2 }}>
                  <Typography variant="body2">
                    Vecchio prezzo: €{priceData.oldPurchasePrice.toFixed(2)}
                  </Typography>
                </Grid>
                
                <Grid item xs={6}>
                  <TextField
                    label="Prezzo di vendita (€)"
                    type="number"
                    fullWidth
                    margin="normal"
                    name="updatedSellingPrice"
                    value={priceData.updatedSellingPrice}
                    onChange={handlePriceFieldChange}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">€</InputAdornment>,
                      inputProps: { step: "0.01", min: 0 }
                    }}
                  />
                </Grid>
                <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center', pl: 2 }}>
                  <Typography variant="body2">
                    Vecchio prezzo: €{priceData.oldSellingPrice.toFixed(2)}
                  </Typography>
                </Grid>
                
                <Grid item xs={6}>
                  <TextField
                    label="Tempo di assemblaggio (min)"
                    type="number"
                    fullWidth
                    margin="normal"
                    name="assemblyTime"
                    value={priceData.assemblyTime}
                    onChange={handlePriceFieldChange}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">min</InputAdornment>,
                      inputProps: { step: "0.1", min: 0 }
                    }}
                  />
                </Grid>
                <Grid item xs={6}></Grid>
                
                <Grid item xs={6}>
                  <TextField
                    label="Prezzo vendita con manodopera (€)"
                    type="number"
                    fullWidth
                    margin="normal"
                    name="sellingPriceWithLabor"
                    value={priceData.sellingPriceWithLabor}
                    onChange={handlePriceFieldChange}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">€</InputAdornment>,
                      inputProps: { step: "0.01", min: 0 }
                    }}
                  />
                </Grid>
                <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center', pl: 2 }}>
                  <Typography variant="body2">
                    Vecchio prezzo: €{priceData.oldSellingPriceWithLabor.toFixed(2)}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button 
            onClick={handleBack} 
            disabled={loading}
            color="secondary"
          >
            Indietro
          </Button>
          <Button 
            onClick={confirmPrices} 
            color="primary" 
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : 'Conferma e procedi'}
          </Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ mt: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
        <Button 
          variant="contained" 
          onClick={handleSubmit}
          disabled={loading || !productId || quantity <= 0 || !purchasePrice}
        >
          {loading ? <CircularProgress size={24} /> : 'Carica'}
        </Button>

        {success && (
          <Alert severity="success" sx={{ flex: 1 }}>
            Carico registrato con successo
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ flex: 1 }}>
            {error}
          </Alert>
        )}
      </Box>
    </Box>
  );
}
